export type EnrollmentConcessionCategory = null | 'discount' | 'scholarship';
export type EnrollmentConcessionValueKind = 'amount' | 'percentage';

/**
 * Z04 / SP-12 / ES14: Enrollment Summary concession math.
 * Discount (concession) covers tuition plus other enrollment charges.
 * Scholarship covers tuition only.
 */
export function calculateManualEnrollmentDiscount(params: {
  discountType: EnrollmentConcessionValueKind;
  discountCategory: EnrollmentConcessionCategory;
  discountAmount: number;
  discountPercentage: number;
  tuitionAmount: number;
  otherCharges?: number;
}): number {
  const tuitionAmount = Math.max(0, params.tuitionAmount || 0);
  const otherCharges = Math.max(0, params.otherCharges || 0);
  const isScholarship = params.discountCategory === 'scholarship';

  if (params.discountType === 'amount') {
    if (isScholarship) {
      return Math.min(Math.max(0, params.discountAmount || 0), tuitionAmount);
    }
    return Math.max(0, params.discountAmount || 0);
  }

  const percent = Math.max(0, params.discountPercentage || 0);
  const base = isScholarship ? tuitionAmount : tuitionAmount + otherCharges;
  return (base * percent) / 100;
}

export function enrollmentConcessionScopeNote(
  discountCategory: EnrollmentConcessionCategory,
): string | null {
  if (discountCategory === 'scholarship') {
    return 'Scholarships apply to tuition only.';
  }
  if (discountCategory === 'discount') {
    return 'Discounts apply to all charges.';
  }
  return null;
}

/**
 * Due-now application of a concession already computed against the full
 * enrollment total.
 *
 * Scholarship: tuition only. Scheduled checkout leaves fees due. Immediate
 * checkout uses the already tuition-capped `discountTotal`.
 * Percent Discount: same rate on due-now charges, not the full-enrollment
 * dollars against a smaller bill.
 * Dollar Discount: may cover due-now fees.
 * API auto discount: allocate across enrollment total when only part is due now.
 */
export function calculateDueNowDiscount(params: {
  discountTotal: number;
  dueNowCharges: number;
  enrollmentTotal?: number;
  discountCategory: EnrollmentConcessionCategory;
  discountType?: EnrollmentConcessionValueKind;
  discountPercentage?: number;
  isClassPaymentScheduled: boolean;
}): number {
  const discountTotal = Math.max(0, params.discountTotal || 0);
  const dueNowCharges = Math.max(0, params.dueNowCharges || 0);
  if (discountTotal <= 0 || dueNowCharges <= 0) {
    return 0;
  }
  if (params.discountCategory === 'scholarship') {
    if (params.isClassPaymentScheduled) {
      return 0;
    }
    return Math.min(discountTotal, dueNowCharges);
  }

  const percent = Math.max(0, params.discountPercentage || 0);
  if (
    params.discountCategory === 'discount' &&
    params.discountType === 'percentage' &&
    percent > 0
  ) {
    return Math.min(dueNowCharges, (dueNowCharges * percent) / 100);
  }

  const enrollmentTotal = Math.max(0, params.enrollmentTotal || 0);
  if (
    params.discountCategory == null &&
    enrollmentTotal > dueNowCharges &&
    enrollmentTotal > 0
  ) {
    return Math.min(
      dueNowCharges,
      (discountTotal * dueNowCharges) / enrollmentTotal,
    );
  }

  return Math.min(discountTotal, dueNowCharges);
}
